import { rowY } from './constants';
import type { PlatformKind, VehicleKind } from './state';

export interface LaneConfig { lane: number; y: number; count: number; width: number; speed: number; period: number; offset: number }
export interface RoadConfig extends LaneConfig { kind: VehicleKind; accelerateAt: number }
export interface WaterConfig extends LaneConfig { kind: PlatformKind; diving: boolean; divePeriod: number; crocEvery: number }
export interface LevelConfig { level: number; pattern: number; roads: RoadConfig[]; water: WaterConfig[]; snakes: number; otter: boolean; homeCroc: boolean }

// Count/type facts: War_Doc Arcade FAQ §6.4. All speed/phase/width/period
// numbers below are independently calibrated estimates, not ROM extraction.
export const PATTERNS = [
  { cars: [3,3,3,1,2], water: [4,3,3,4,3], fast: true,  croc: 0, snakes: 0 },
  { cars: [4,4,3,2,3], water: [3,3,1,4,3], fast: true,  croc: 5, snakes: 0 },
  { cars: [4,4,5,2,3], water: [3,3,1,4,2], fast: false, croc: 3, snakes: 1 },
  { cars: [4,4,4,3,4], water: [3,2,1,3,2], fast: true,  croc: 2, snakes: 1 },
  { cars: [5,4,5,4,3], water: [2,2,1,3,2], fast: false, croc: 1, snakes: 1 },
  { cars: [3,3,3,1,2], water: [4,3,3,4,2], fast: false, croc: 2, snakes: 1 },
  { cars: [4,4,4,2,3], water: [3,3,1,5,2], fast: true,  croc: 2, snakes: 2 },
  { cars: [4,4,5,2,3], water: [3,3,1,4,1], fast: true,  croc: 2, snakes: 2 },
  { cars: [4,4,4,3,4], water: [3,2,1,3,1], fast: true,  croc: 2, snakes: 2 },
  { cars: [5,4,5,4,4], water: [2,2,1,3,2], fast: false, croc: 1, snakes: 2 },
] as const;
const ROAD_SPEEDS = [
  [24,22,28,66,19], [27,24,31,74,22], [28,26,34,32,24], [30,28,36,82,25], [31,30,38,35,26],
  [25,24,30,32,22], [29,26,34,78,24], [31,28,36,82,26], [32,30,38,86,27], [34,31,40,38,28],
] as const;
const WATER_SPEEDS = [
  [24,22,32,24,20], [26,24,34,26,22], [28,25,36,28,24], [29,27,38,29,26], [31,28,40,31,27],
  [26,24,34,25,23], [28,26,36,27,25], [30,27,38,29,27], [32,29,40,30,28], [33,30,42,32,30],
] as const;

export function levelConfig(level: number): LevelConfig {
  const n = Math.max(1, Math.floor(level));
  const pattern = n <= 10 ? n : 6 + (n - 11) % 5;
  const p = PATTERNS[pattern - 1]!;
  const kinds: VehicleKind[] = ['car','sedan','buggy','race','truck'];
  const roads = p.cars.map((count, i): RoadConfig => ({
    lane: i + 1, y: rowY(i + 1), count, kind: kinds[i]!, width: [16,22,18,16,32][i]!,
    speed: ROAD_SPEEDS[pattern-1]![i]! * (i % 2 === 0 ? -1 : 1), period: 256,
    offset: [4,34,12,150,62][i]!, accelerateAt: i === 3 && !p.fast ? 12 * 60 : Infinity,
  }));
  const water = p.water.map((count, i): WaterConfig => {
    const kind: PlatformKind = i === 0 || i === 3 ? 'turtle' : 'log';
    const width = i === 0 ? 42 : i === 1 ? 48 : i === 2 ? (count === 1 ? 168 : 80) : i === 3 ? 28 : 64;
    return { lane: i+1, y: rowY(7+i), count, width, kind,
      speed: WATER_SPEEDS[pattern-1]![i]! * ([ -1,1,1,-1,1 ][i]!),
      period: Math.max(256, count * (width + (i === 2 ? 16 : 14))),
      offset: [8,24,-36,12,-12][i]!, diving: kind === 'turtle',
      divePeriod: 480 - (pattern % 5) * 24, crocEvery: i === 4 ? p.croc : 0 };
  });
  return { level: n, pattern, roads, water, snakes: p.snakes, homeCroc: n >= 2, otter: n >= 4 };
}
